import { SxProps } from '@mui/material'
import React, { useMemo } from 'react'
import {
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    Checkbox,
    TableBody,
    Paper,
    TablePagination,
    TableSortLabel,
    TableFooter,
    Box,
} from '@mui/material'

import { IHeader } from './IHeader'
import HelperTooltip from 'components/Tooltip/HelperTooltip'
import { isSelectable } from './data-table.hook'
import { useDataTableContext } from './DataTableContext'
import CollapseCell from './Rows/CollapseCell'
import RowPart from './Rows/RowPart'
import DroppableComponent from './Rows/DroppableComponent'
import { isEmpty } from 'lodash-es'

const stickyColumn: SxProps = {
    position: 'sticky',
    zIndex: 3,
    backgroundColor: '#f2f6f8',
}

function DataTableContainer() {
    const {
        tableMaxHeight,
        stickyHeader,
        hiddenHeader,
        hasCollapseData,
        headers,
        selectMode = true,
        selected = [],
        data,
        rowOption,
        forceIsSelectedAll,
        onSelectAll,
        disabled = false,
        sortColOrders,
        cellPadding,
        updateSortHandler,
        onDragDrop,
        footer,
        usePagination,
        page,
        rowPerPageOptions = [5, 10, 25],
        rowPerPage,
        handleOnChangePage,
        handleOnChangeRowPerPage,
        onAllCollapse,
        openCollapses,
        noExpandableItems,
    } = useDataTableContext()

    const count = data.length

    const colunmStyle = (isSticky: boolean) => {
        return isSticky ? stickyColumn : undefined
    }
    const rowOptionResolved = useMemo(() => rowOption || {}, [rowOption])

    const selectableRowCount = useMemo(() => {
        return data.filter((e) => {
            return isSelectable(e, rowOptionResolved)
        }).length
    }, [data, rowOptionResolved])

    let totalLeft = 0
    let isHiddenColumn = false

    return (
        <Box width={'100%'}>
            <TableContainer
                component={Paper}
                sx={{
                    height: tableMaxHeight,
                    overflow: 'auto',
                }}
            >
                <Table stickyHeader={stickyHeader}>
                    <TableHead>
                        <TableRow
                            style={{
                                visibility: hiddenHeader
                                    ? 'collapse'
                                    : 'inherit',
                            }}
                        >
                            {hasCollapseData && (
                                <TableCell
                                    padding="checkbox"
                                    sx={colunmStyle(
                                        headers.some((i) => i.isSticky),
                                    )}
                                >
                                    <CollapseCell
                                        onAllCollapse={onAllCollapse}
                                        isAllCollapse={
                                            openCollapses.length === 0
                                        }
                                        // Expand/Collapse buttons will be disabled if no data or all data are unexpandable
                                        disabled={
                                            isEmpty(data) || noExpandableItems
                                        }
                                    />
                                </TableCell>
                            )}

                            {selectMode && (
                                <TableCell padding="checkbox">
                                    <Checkbox
                                        indeterminate={
                                            selected.length <
                                                selectableRowCount &&
                                            selected.length > 0
                                        }
                                        checked={
                                            forceIsSelectedAll ||
                                            (selected.length >=
                                                selectableRowCount &&
                                                selected.length > 0)
                                        }
                                        onClick={() => {
                                            if (onSelectAll) onSelectAll()
                                        }}
                                        color="primary"
                                        disabled={disabled}
                                    ></Checkbox>
                                </TableCell>
                            )}

                            {headers.map((item: IHeader, index: number) => {
                                totalLeft += headers[index - 1]?.width ?? 0
                                isHiddenColumn = item.isDisabledRow ?? false

                                return (
                                    !isHiddenColumn && (
                                        <TableCell
                                            sx={colunmStyle(
                                                item.isSticky ?? false,
                                            )}
                                            key={index}
                                            sortDirection={
                                                sortColOrders?.find(
                                                    (o) =>
                                                        o.column.toLowerCase() ===
                                                        item.label
                                                            ?.toString()
                                                            .toLowerCase(),
                                                )?.direction
                                            }
                                            style={{
                                                ['textAlign' as any]: item.align
                                                    ? item.align
                                                    : undefined,
                                                padding:
                                                    item.headerPadding ??
                                                    cellPadding,
                                                width: item.width,
                                                left: item.isSticky
                                                    ? totalLeft
                                                    : 'initial',
                                            }}
                                        >
                                            {item.sortable ? (
                                                <TableSortLabel
                                                    active={
                                                        sortColOrders?.some(
                                                            (o) =>
                                                                o.column.toLowerCase() ===
                                                                item.label
                                                                    ?.toString()
                                                                    .toLowerCase(),
                                                        ) ?? false
                                                    }
                                                    direction={
                                                        sortColOrders?.find(
                                                            (o) =>
                                                                o.column.toLowerCase() ===
                                                                item.label
                                                                    ?.toString()
                                                                    .toLowerCase(),
                                                        )?.direction ?? 'asc'
                                                    }
                                                    onClick={() =>
                                                        updateSortHandler(
                                                            item.label
                                                                ?.toString()
                                                                .toLowerCase(),
                                                        )
                                                    }
                                                    sx={
                                                        item.align === 'end' ||
                                                        item.type === 'number'
                                                            ? {
                                                                  flexDirection:
                                                                      'row-reverse',
                                                              }
                                                            : {}
                                                    }
                                                >
                                                    <b>
                                                        <HelperTooltip
                                                            helperText={
                                                                item.helpText
                                                            }
                                                        >
                                                            <Box
                                                                mr={
                                                                    item.align ===
                                                                        'end' ||
                                                                    item.type ===
                                                                        'number'
                                                                        ? 0
                                                                        : 1
                                                                }
                                                                color={
                                                                    disabled
                                                                        ? 'GrayText'
                                                                        : undefined
                                                                }
                                                            >
                                                                {item.label}
                                                            </Box>
                                                        </HelperTooltip>
                                                    </b>
                                                </TableSortLabel>
                                            ) : (
                                                <b>
                                                    <HelperTooltip
                                                        justifyContent={
                                                            item.align ===
                                                            'center'
                                                                ? 'center'
                                                                : undefined
                                                        }
                                                        helperText={
                                                            item.helpText
                                                        }
                                                        style={{
                                                            float:
                                                                item.align ===
                                                                    'end' ||
                                                                item.type ===
                                                                    'number'
                                                                    ? 'right'
                                                                    : undefined,
                                                        }}
                                                    >
                                                        <Box
                                                            mr={
                                                                item.align ===
                                                                    'center' ||
                                                                item.align ===
                                                                    'end' ||
                                                                item.type ===
                                                                    'number'
                                                                    ? 0
                                                                    : 1
                                                            }
                                                            color={
                                                                disabled
                                                                    ? 'GrayText'
                                                                    : undefined
                                                            }
                                                        >
                                                            {item.label}
                                                        </Box>
                                                    </HelperTooltip>
                                                </b>
                                            )}
                                        </TableCell>
                                    )
                                )
                            })}
                        </TableRow>
                    </TableHead>
                    {onDragDrop ? (
                        <TableBody component={DroppableComponent}>
                            <RowPart />
                        </TableBody>
                    ) : (
                        <TableBody>
                            <RowPart />
                        </TableBody>
                    )}
                    {footer && <TableFooter>{footer}</TableFooter>}
                </Table>
            </TableContainer>

            {usePagination && (
                <TablePagination
                    component={'div'}
                    count={count}
                    page={page}
                    rowsPerPageOptions={[
                        ...rowPerPageOptions,
                        { label: 'All', value: -1 },
                    ]}
                    rowsPerPage={rowPerPage}
                    onPageChange={handleOnChangePage}
                    onRowsPerPageChange={handleOnChangeRowPerPage}
                />
            )}
        </Box>
    )
}
export default DataTableContainer
